import {useState, useEffect, useCallback} from 'react'
import {useRouteMatch} from 'react-router'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../ssr/constants'
import {getValidToken} from '../utils/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useLocaleCountry} from '../hooks/use-locale-custom'

const useOrderDetails = (guest = false) => {
    const appOrigin = useOrigin(false)
    const {data: customer, isFetched: isCustomerFetched} = useCurrentCustomer()
    const [isLoading, setIsLoading] = useState(false)
    const [order, setOrder] = useState(true)
    const {params} = useRouteMatch()
    const country = useLocaleCountry()
    const {orderId, orderNo, originalOrderId} = params

    const fetchToken = useCallback(async () => {
        const apiUrl = `${appOrigin}${MRT_API_BASEPATH}/get-auth-token-oms`
        const accessTokenKey = 'accessTokenOMS'
        const tokenExpirationKey = 'tokenExpirationOMS'
        return await getValidToken(apiUrl, accessTokenKey, tokenExpirationKey)
    }, [appOrigin])

    const fetchOrderDetails = useCallback(async () => {
        try {
            setIsLoading(true)
            const accessToken = await fetchToken()
            const queryParams = new URLSearchParams({
                orderSummaryId: orderId,
                gigyaId: customer?.c_gigyaId,
                orderNumber: orderNo,
                originalOrderId: originalOrderId,
                country: country,
                email: customer?.email,
                guest: guest,
            })

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/get-order-details-oms?${queryParams.toString()}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            )
            const result = await response.json()
            if (result.success) {
                setOrder({
                    ...result.order,
                    // fulfillmentOrder: [
                    //     {
                    //         ExternalId__c: 'SP123456', // Unique External ID for the shipment
                    //         Status: 'Shipped', // Status of the shipment (could be "Shipped", "Pending", etc.)
                    //         TotalAmount: 150.0, // Total amount for the shipment
                    //         FulfillmentOrderLineItems: {
                    //                records: [
                    //                     {
                    //                         OrderItemSummaryId: '10uKM0000008OMgYAM', // Unique Order Item ID
                    //                         DeliveryDate__c: '2025-03-22', // Expected delivery date
                    //                         Quantity: 2, // Quantity of items in this shipment
                    //                         TrackingNumber__c: '1Z12345E0205271688', // Tracking number for the shipment
                    //                         TrackingURL__c:
                    //                             'https://www.carrier.com/track/1Z12345E0205271688', // URL for tracking the shipment
                    //                     },
                    //                 ],
                    //             },
                    //         },
                    //         {
                    //             ExternalId__c: 'SP123455', // Another shipment's External ID
                    //             Status: 'Pending', // The shipment is not yet shipped, status is "Pending"
                    //             TotalAmount: 75.0, // Total amount for the second shipment
                    //             FulfillmentOrderLineItems: {
                    //                 records: [
                    //                     {
                    //                         OrderItemSummaryId: '10uKM0000008OMiYAM', // Order Item ID for the second shipment
                    //                         DeliveryDate__c: '2025-03-23', // Expected delivery date for this shipment
                    //                         Quantity: 1, // Quantity of items for this shipment
                    //                         TrackingNumber__c: '1Z12345E0205271689', // Tracking number for this shipment
                    //                         TrackingURL__c:
                    //                             'https://www.carrier.com/track/1Z12345E0205271689', // Tracking URL for the shipment
                    //                     },
                    //                 ],
                    //        },
                    //     },
                    // ],
                    // fiscalDocuments: [
                    //     {
                    //         Id: '00PMA000003C9Lz2AK',
                    //         Name: 'SP123456',
                    //     },
                    //     {
                    //         Id: '00PMA000003C9Lz2AK',
                    //         Name: 'SP123455',
                    //     },
                    // ],
                })
            }
        } catch (error) {
            // TODO:
            console.error('Error fetching order details:', error)
        } finally {
            setIsLoading(false)
        }
    }, [orderId, orderNo, originalOrderId, customer?.c_gigyaId, fetchToken])

    useEffect(() => {
        if (!guest && !isCustomerFetched) {
            return
        }
        if (!orderId && (!customer?.c_gigyaId || !orderNo)) return
        fetchOrderDetails()
    }, [isCustomerFetched, orderId, customer?.c_gigyaId, orderNo, fetchOrderDetails])

    const refetchOrder = useCallback(() => {
        fetchOrderDetails()
    }, [fetchOrderDetails])

    return {order, isLoading, refetchOrder}
}

/**
 * Hook per ottenere i dettagli di un ordine basandosi su un oggetto `order` passato come argomento.
 * @param {Object} order - Oggetto ordine contenente i parametri necessari.
 * @returns {Object} - Dettagli dell'ordine, stato di caricamento e eventuali errori.
 */
const useOrderDetailsFromOrder = (order, guest = false) => {
    const [orderDetails, setOrderDetails] = useState(null)
    const {data: customer} = useCurrentCustomer()
    const country = useLocaleCountry()
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const appOrigin = useOrigin(false)

    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!order?.Id || !order?.OrderNumber || !order?.OriginalOrderId) {
                setError('Missing required order parameters')
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)

                const apiUrl = `${appOrigin}${MRT_API_BASEPATH}/get-auth-token-oms`
                const accessTokenKey = 'accessTokenOMS'
                const tokenExpirationKey = 'tokenExpirationOMS'
                const accessToken = await getValidToken(apiUrl, accessTokenKey, tokenExpirationKey)

                const queryParams = new URLSearchParams({
                    orderSummaryId: order.Id,
                    orderNumber: order.OrderNumber,
                    originalOrderId: order.OriginalOrderId,
                    gigyaId: customer?.c_gigyaId,
                    country: country,
                    email: customer?.email,
                    guest: guest,
                })

                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-order-details-oms?${queryParams.toString()}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessToken}`,
                        },
                    },
                )

                const result = await response.json()
                if (result.success) {
                    setOrderDetails(result.order)
                } else {
                    setError('Failed to fetch order details: ' + result.error)
                }
            } catch (err) {
                console.error('Error fetching order details:', err)
                setError('Error fetching order details: ' + err.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (order) {
            fetchOrderDetails()
        }
    }, [order, appOrigin])

    return {orderDetails, isLoading, error}
}

export {useOrderDetails, useOrderDetailsFromOrder}
